import { Loader2, Zap } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type ComponentType, memo, type SVGProps, useEffect, useState } from 'react';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { BaseLineShare } from '@/app/components/icons/BaseLineShare';
import { Button } from '@/app/components/ui/button';
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from '@/app/components/ui/hover-card';
import { Input } from '@/app/components/ui/input';
import { toast } from '@/app/components/ui/use-toast';
import { useGlobalModal } from '@/app/hooks';
import { cn, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { useSessionActions } from '../../_hook/use-session-actions';
import type { SessionMode } from '../../types';

export interface NavItem {
  id: string;
  type: 'session' | 'file' | 'cases';
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  path: string;
  /** 是否需要 activeClaw 才展示，默认为 true */
  requiresActiveClaw?: boolean;
  /** 点击时上报的 element_name，未配置则不上报 */
  elementName?: string;
  mode?: SessionMode;
  hasUnread?: boolean;
  isLoadingUnread?: boolean;
}

interface NavButtonProps {
  item: NavItem;
  pathname: string;
  activeSessionId: string | null;
  onBeforeNavigate?: () => void;
}

export const NavButton = memo(
  ({ item, pathname, activeSessionId, onBeforeNavigate }: NavButtonProps) => {
    const t = useTranslations();
    const router = useRouter();
    const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
    const [isMoreOpen, setIsMoreOpen] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editingTitle, setEditingTitle] = useState(item.label);
    const { renamingSession, deletingSession, renameSession, deleteSession } = useSessionActions();
    const Icon = item.icon;

    useEffect(() => {
      if (!isEditingTitle) {
        setEditingTitle(item.label);
      }
    }, [item.label, isEditingTitle]);

    const isActive = item.path === '/claw' ? pathname === '/claw' && !activeSessionId : pathname === item.path;

    const isSessionActionAvailable = item.type === 'session' && item.id !== 'session';

    const submitEditingTitle = async () => {
      if (!isSessionActionAvailable || renamingSession) return;
      const nextTitle = editingTitle.trim();

      if (!nextTitle) {
        toast({ type: 'error', message: t('claw.nav.sessionNameRequired') });
        return;
      }

      if (nextTitle === item.label) {
        setIsEditingTitle(false);
        return;
      }

      const response = await renameSession({
        sessionId: item.id,
        name: nextTitle,
      });

      if (response.error) {
        toast({ type: 'error', message: response.error.message });
        return;
      }

      setIsEditingTitle(false);
      toast({ type: 'success', message: t('claw.nav.renameSuccess') });
    };

    const handleDeleteSession = () => {
      if (!isSessionActionAvailable || deletingSession) return;
      openConfirmDialog({
        inheritTheme: true,
        content: t('claw.nav.deleteConfirm'),
        secondaryButtonText: t('common.dialog.cancel'),
        primaryButtonText: t('common.dialog.confirm'),
        onPrimaryAction: async () => {
          const response = await deleteSession(item.id);
          if (response.error) {
            toast({ type: 'error', message: response.error.message });
            return;
          }
          toast({ type: 'success', message: t('claw.nav.deleteSuccess') });
        },
      });
    };

    return (
      <Button
        variant="text"
        className={cn(
          'group flex w-full cursor-pointer justify-start gap-3 rounded-12 p-3 transition-colors',
          isActive
            ? 'bg-system-white16 light:bg-[rgba(87,101,242,0.16)] light:text-[#5765F2] text-system-white'
            : 'text-system-white80 hover:bg-system-white12 light:hover:bg-[rgba(87,101,242,0.12)]',
        )}
        onClick={() => {
          if (item.elementName) {
            reportEvent('claw_element_click', { element_name: item.elementName });
          }
          onBeforeNavigate?.();
          router.push(item.path);
        }}
      >
        {item.mode === 'yolo' ? (
          <span className="flex size-5 items-center justify-center rounded-6 bg-[#5765F266]">
            <Zap className="size-3 shrink-0 text-white" />
          </span>
        ) : (
          <Icon className={cn('size-5', isActive ? 'light:text-[#5765F2] text-system-white' : 'text-system-white36')} />
        )}
        {isEditingTitle ? (
          <div
            className="flex h-7 flex-1 items-center gap-1 rounded-8 border border-[#5765F2] bg-system-white08 px-2"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Input
              value={editingTitle}
              autoFocus={true}
              disabled={renamingSession}
              maxLength={100}
              className="h-auto flex-1 border-none bg-transparent px-0 py-0 font-normal text-sm text-system-white leading-5 tracking-[-0.15px] focus-visible:ring-0"
              onChange={(event) => {
                setEditingTitle(event.target.value);
              }}
              onBlur={() => {
                submitEditingTitle();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submitEditingTitle();
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setEditingTitle(item.label);
                  setIsEditingTitle(false);
                }
              }}
            />
            <span className="h-4 w-px bg-system-white16" />
          </div>
        ) : (
          <div
            className={cn(
              'flex min-w-0 flex-1 items-center gap-2 text-left font-medium text-sm',
              isActive ? 'light:text-[#5765F2] text-system-white' : 'font-normal',
            )}
          >
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
          </div>
        )}
        {isSessionActionAvailable && !isEditingTitle ? (
          <span className="relative flex size-6 shrink-0 items-center justify-center">
            {!isMoreOpen &&
              (item.isLoadingUnread ? (
                <Loader2 className="size-3.5 shrink-0 animate-spin text-system-white40 [animation-duration:2s] group-hover:hidden" />
              ) : (
                item.hasUnread && <span className="size-1.5 shrink-0 rounded-full bg-[#707DFF] group-hover:hidden" />
              ))}
            <HoverCard open={isMoreOpen} onOpenChange={setIsMoreOpen} openDelay={0} closeDelay={120}>
              <HoverCardTrigger asChild={true} onClick={(event) => event.stopPropagation()}>
                <span
                  className={cn(
                    'absolute inset-0 flex items-center justify-center rounded-full text-system-white40 transition-colors',
                    'hover:bg-system-white16 hover:text-system-white',
                    isMoreOpen
                      ? 'bg-system-white16 text-system-white'
                      : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100',
                  )}
                >
                  <BaseLineMore className="size-4" />
                </span>
              </HoverCardTrigger>
              <HoverCardPortal>
                <HoverCardContent
                  align="end"
                  side="bottom"
                  sideOffset={8}
                  className="w-32 rounded-12 border border-system-white08 bg-[rgba(34,37,42,0.64)] light:bg-system-bg02 p-1 light:text-system-text01 text-system-white light:shadow-[0_4px_20px_0_rgba(0,0,0,0.18)] shadow-[0_4px_20px_0_rgba(0,0,0,0.4)] backdrop-blur-[30px]"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Button
                    variant="text"
                    className="flex h-9 w-full cursor-pointer items-center gap-2 rounded-8 px-3 text-left light:text-system-text01 text-sm text-system-white hover:bg-system-white08"
                    onClick={() => {
                      setEditingTitle(item.label);
                      setIsEditingTitle(true);
                      setIsMoreOpen(false);
                    }}
                  >
                    <BaseLineEdit className="size-4 light:text-system-text04 text-system-white40" />
                    <span>{t('claw.nav.editTitle')}</span>
                  </Button>
                  <Button
                    variant="text"
                    className="flex h-9 w-full cursor-pointer items-center gap-2 rounded-8 px-3 text-left light:text-system-text01 text-sm text-system-white hover:bg-system-white08 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => {
                      setIsMoreOpen(false);
                      router.push(`${item.path}?share=1`);
                    }}
                  >
                    <BaseLineShare className="size-4 light:text-system-text04 text-system-white40" />
                    <span>{t('claw.nav.share')}</span>
                  </Button>
                  <Button
                    variant="text"
                    disabled={deletingSession}
                    className="flex h-9 w-full cursor-pointer items-center gap-2 rounded-8 px-3 text-left text-[#F04141] text-sm hover:bg-[rgba(240,65,65,0.08)] disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => {
                      setIsMoreOpen(false);
                      handleDeleteSession();
                    }}
                  >
                    <BaseLineDelete className="size-4 text-[#F04141]" />
                    <span>{t('claw.nav.delete')}</span>
                  </Button>
                </HoverCardContent>
              </HoverCardPortal>
            </HoverCard>
          </span>
        ) : null}
      </Button>
    );
  },
  (prev, next) => {
    return (
      prev.pathname === next.pathname &&
      prev.activeSessionId === next.activeSessionId &&
      prev.onBeforeNavigate === next.onBeforeNavigate &&
      prev.item.id === next.item.id &&
      prev.item.type === next.item.type &&
      prev.item.path === next.item.path &&
      prev.item.label === next.item.label &&
      prev.item.icon === next.item.icon &&
      prev.item.mode === next.item.mode &&
      prev.item.hasUnread === next.item.hasUnread &&
      prev.item.isLoadingUnread === next.item.isLoadingUnread
    );
  },
);

NavButton.displayName = 'NavButton';
